import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  Card,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableRow,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DelteIcon from '@material-ui/icons/Delete';
import EnhancedTableHead from '../EnhancedTableHead';

const useStyles = makeStyles({
  tableCell: {
    cursor: 'pointer',
  },
  loader: {
    position: 'absolute !important',
    top: '20px',
    left: '45%',
  },
  tableBody: {
    position: 'relative',
    height: '60px',
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
});

const headCells = [
  { id: 'no', numeric: false, disablePadding: false, label: 'No' },
  {
    id: 'firstname',
    numeric: false,
    disablePadding: false,
    label: 'First Name',
    sortable: true,
  },
  {
    id: 'lastname',
    numeric: false,
    disablePadding: false,
    label: 'Last Name',
    sortable: true,
  },
  {
    id: 'email',
    numeric: false,
    disablePadding: false,
    label: 'Email',
    sortable: true,
  },
  {
    id: 'phonenumber',
    numeric: false,
    disablePadding: false,
    label: 'Phone Number',
    sortable: true,
  },

  {
    id: 'class',
    numeric: false,
    disablePadding: false,
    label: 'Class',
    sortable: true,
  },
  {
    id: 'description',
    numeric: false,
    disablePadding: false,
    label: 'Description',
    sortable: true,
  },
  {
    id: 'colour',
    numeric: false,
    disablePadding: false,
    label: 'Colour',
    sortable: true,
  },
  {
    id: 'license_plate',
    numeric: false,
    disablePadding: false,
    label: 'License Plate',
    sortable: true,
  },
  {
    id: 'status',
    numeric: false,
    disablePadding: false,
    label: 'Status',
    sortable: true,
  },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }

  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const VehiclesListResults = ({
  vehicles,
  handleRow,
  loading,
  pagerInfo,
  handlePagerInfo,
  actionHandler = true,
  isPagination = true,
  ...rest
}) => {
  const classes = useStyles();
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('calories');
  const navigate = useNavigate();

  const handleLimitChange = (event) => {
    handlePagerInfo({
      limit: event.target.value,
      page: 1,
      totalSize: 0,
    });
  };

  const handlePageChange = (event, newPage) => {
    handlePagerInfo({
      ...pagerInfo,
      page: newPage + 1,
    });
  };

  const goToEdit = (id) => {
    navigate(`/app/vehicles/edit/${id}`, { state: id });
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  let newHeadCells = [];

  if (actionHandler) {
    newHeadCells = [
      ...headCells,
      {
        id: 'action',
        numeric: false,
        disablePadding: false,
        label: 'Action',
        sortable: false,
      },
    ];
  }

  return (
    <Card {...rest}>
      <PerfectScrollbar>
        <Box>
          <Table>
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              headCells={newHeadCells}
              onRequestSort={handleRequestSort}
            />
            <TableBody className={classes.tableBody}>
              {loading && <CircularProgress className={classes.loader} />}
              {!loading &&
                vehicles &&
                stableSort(vehicles, getComparator(order, orderBy)).map(
                  (item, index) => (
                    <TableRow
                      hover
                      key={item.id}
                      onClick={() => goToEdit(item.id)}
                    >
                      <TableCell>
                        {isPagination
                          ? (pagerInfo.page - 1) * pagerInfo.limit + index + 1
                          : index + 1}
                      </TableCell>
                      <TableCell>{item.firstname}</TableCell>
                      <TableCell>{item.lastname}</TableCell>
                      <TableCell>{item.email}</TableCell>
                      <TableCell>{item.phonenumber}</TableCell>
                      <TableCell>{item.class}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>{item.colour}</TableCell>
                      <TableCell>{item.license_plate}</TableCell>
                      <TableCell>{item.status.toUpperCase()}</TableCell>
                      {actionHandler && (
                        <TableCell>
                          <EditIcon
                            style={{ cursor: 'pointer' }}
                            onClick={() => goToEdit(item.id)}
                          />
                          <DelteIcon
                            style={{ cursor: 'pointer', color: 'indianred' }}
                            onClick={() => handleRow(item.id)}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  )
                )}
              {!loading && (!vehicles || vehicles.length === 0) && (
                <Typography className={classes.loader}>No Data</Typography>
              )}
            </TableBody>
          </Table>
        </Box>
      </PerfectScrollbar>
      {isPagination && (
        <TablePagination
          component="div"
          count={pagerInfo ? pagerInfo.totalSize : 0}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleLimitChange}
          page={pagerInfo ? pagerInfo.page - 1 : 0}
          rowsPerPage={pagerInfo ? pagerInfo.limit : 0}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
        />
      )}
    </Card>
  );
};

VehiclesListResults.propTypes = {
  vehicles: PropTypes.array,
  handleRow: PropTypes.func,
  loading: PropTypes.bool,
  actionHandler: PropTypes.bool,
  pagerInfo: PropTypes.object,
  handlePagerInfo: PropTypes.func,
  isPagination: PropTypes.bool,
};

export default VehiclesListResults;
